import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  PhoneOutgoing, Users, Clock, Settings, Play, CheckCircle,
  XCircle, AlertTriangle, RefreshCw, Phone
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  initiated: { label: 'Avviata', color: 'bg-blue-100 text-blue-700', icon: Phone },
  call_placed: { label: 'In corso', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  completed: { label: 'Completata', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  failed: { label: 'Fallita', color: 'bg-red-100 text-red-700', icon: XCircle },
  error: { label: 'Errore', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

export default function Recall() {
  const { token } = useAuth();
  const [tab, setTab] = useState('candidates');
  const [candidates, setCandidates] = useState([]);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState({ enabled: false, days_inactive: 90, max_calls_per_day: 10, call_hours_start: 9, call_hours_end: 18 });
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState({});
  const [daysFilter, setDaysFilter] = useState(90);
  const [selected, setSelected] = useState(new Set());

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cRes, hRes, sRes] = await Promise.all([
        fetch(`${API}/api/recall/candidates?days=${daysFilter}`, { headers }),
        fetch(`${API}/api/recall/history`, { headers }),
        fetch(`${API}/api/recall/settings`, { headers }),
      ]);
      if (cRes.ok) { const d = await cRes.json(); setCandidates(d.candidates || []); }
      if (hRes.ok) setHistory(await hRes.json());
      if (sRes.ok) setSettings(await sRes.json());
    } catch {} finally { setLoading(false); }
  };

  const handleCall = async (patientId) => {
    setCalling(prev => ({ ...prev, [patientId]: true }));
    try {
      const res = await fetch(`${API}/api/recall/call`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: patientId })
      });
      if (res.ok) { const d = await res.json(); toast.success(d.message || 'Chiamata avviata!'); loadAll(); }
      else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch { toast.error('Errore di rete'); }
    finally { setCalling(prev => ({ ...prev, [patientId]: false })); }
  };

  const handleBatchCall = async () => {
    if (selected.size === 0) { toast.error('Seleziona almeno un paziente'); return; }
    setCalling(prev => ({ ...prev, batch: true }));
    try {
      const res = await fetch(`${API}/api/recall/call-batch`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_ids: Array.from(selected) })
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`${d.success} chiamate avviate su ${d.total}`);
        setSelected(new Set());
        loadAll();
      } else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch { toast.error('Errore'); }
    finally { setCalling(prev => ({ ...prev, batch: false })); }
  };

  const handleSaveSettings = async () => {
    try {
      const res = await fetch(`${API}/api/recall/settings`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (res.ok) toast.success('Impostazioni salvate!');
      else toast.error('Errore nel salvataggio');
    } catch { toast.error('Errore'); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const selectAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map(c => c.id)));
  };

  return (
    <Layout>
      <div className="p-4 sm:p-8 max-w-5xl" data-testid="recall-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <PhoneOutgoing className="w-6 h-6 text-orange-500" /> Recall Automatico
            </h1>
            <p className="text-sm text-slate-500 mt-1">Richiama pazienti inattivi con l'AI vocale</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1 -mx-1 px-1">
          {[{ id: 'candidates', label: 'Pazienti da richiamare', icon: Users },
            { id: 'history', label: 'Storico chiamate', icon: Clock },
            { id: 'settings', label: 'Impostazioni', icon: Settings }
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap shrink-0 ${
                tab === t.id ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16"><div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : (
          <>
            {/* CANDIDATES TAB */}
            {tab === 'candidates' && (
              <div>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-600">Inattivi da:</span>
                    <select value={daysFilter} onChange={e => { setDaysFilter(Number(e.target.value)); }}
                      className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm" data-testid="days-filter">
                      <option value={30}>30 giorni</option>
                      <option value={60}>60 giorni</option>
                      <option value={90}>90 giorni</option>
                      <option value={180}>6 mesi</option>
                      <option value={365}>1 anno</option>
                    </select>
                    <Button variant="outline" size="sm" onClick={loadAll}><RefreshCw className="w-4 h-4" /></Button>
                  </div>
                  {selected.size > 0 && (
                    <Button onClick={handleBatchCall} disabled={calling.batch} className="bg-orange-500 hover:bg-orange-600" data-testid="batch-call-btn">
                      <Play className="w-4 h-4 mr-1" /> Chiama selezionati ({selected.size})
                    </Button>
                  )}
                </div>

                {candidates.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                    <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">Nessun paziente da richiamare</p>
                    <p className="text-xs text-slate-400 mt-1">Tutti i pazienti hanno appuntamenti recenti</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
                      <input type="checkbox" checked={selected.size === candidates.length && candidates.length > 0}
                        onChange={selectAll} className="rounded border-slate-300" />
                      <span className="text-xs text-slate-500 font-medium">{candidates.length} pazienti trovati</span>
                    </div>
                    {candidates.map(c => (
                      <div key={c.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`candidate-${c.id}`}>
                        <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} className="rounded border-slate-300" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 text-sm">{c.name}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                            <span>{c.phone}</span>
                            <span>Ultima visita: {c.last_appointment}</span>
                          </div>
                        </div>
                        <Button size="sm" onClick={() => handleCall(c.id)} disabled={calling[c.id]}
                          className="bg-orange-500 hover:bg-orange-600 text-xs" data-testid={`call-${c.id}`}>
                          {calling[c.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <><Phone className="w-3 h-3 mr-1" /> Chiama</>}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* HISTORY TAB */}
            {tab === 'history' && (
              <div className="space-y-3">
                {history.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                    <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">Nessuna chiamata recall effettuata</p>
                  </div>
                ) : history.map((h, i) => {
                  const st = STATUS_MAP[h.status] || STATUS_MAP.error;
                  const StIcon = st.icon;
                  return (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`history-${i}`}>
                      <StIcon className="w-5 h-5 text-slate-400 shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-slate-800 text-sm">{h.patient_name}</p>
                        <p className="text-xs text-slate-500">{h.patient_phone}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${st.color}`}>{st.label}</span>
                      <span className="text-xs text-slate-400">{h.created_at ? new Date(h.created_at).toLocaleString('it-IT') : ''}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* SETTINGS TAB */}
            {tab === 'settings' && (
              <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-lg space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-800">Recall Automatico</p>
                    <p className="text-xs text-slate-500">Abilita le chiamate automatiche ai pazienti inattivi</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" checked={settings.enabled}
                      onChange={e => setSettings({...settings, enabled: e.target.checked})} data-testid="recall-enabled" />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500" />
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Giorni di inattivita'</label>
                  <input type="number" value={settings.days_inactive} min={7} max={365}
                    onChange={e => setSettings({...settings, days_inactive: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="recall-days" />
                  <p className="text-xs text-slate-400 mt-1">Richiama pazienti senza appuntamento da questo numero di giorni</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Max chiamate al giorno</label>
                  <input type="number" value={settings.max_calls_per_day} min={1} max={100}
                    onChange={e => setSettings({...settings, max_calls_per_day: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="recall-max" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Dalle ore</label>
                    <input type="number" value={settings.call_hours_start} min={7} max={21}
                      onChange={e => setSettings({...settings, call_hours_start: Number(e.target.value)})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Alle ore</label>
                    <input type="number" value={settings.call_hours_end} min={7} max={21}
                      onChange={e => setSettings({...settings, call_hours_end: Number(e.target.value)})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                  </div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-700">
                  <p className="font-medium mb-1">Come funziona il CLI italiano</p>
                  <p>Quando l'AI chiama il paziente, come numero chiamante viene mostrato il numero italiano della clinica.</p>
                </div>
                <Button onClick={handleSaveSettings} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="save-recall-settings">
                  Salva Impostazioni
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
